const JUSTLOCK_API_BASE_URL = 'https://api.justlock.io';

const parseFiniteNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getResourceBalance = (lock, resourceAddress) => {
  if (!lock || !resourceAddress) {
    return 0;
  }

  const balances = lock.token_balances_locked;
  if (!balances || typeof balances !== 'object') {
    return 0;
  }

  return parseFiniteNumber(balances[resourceAddress]);
};

export const parseLockIds = (lockIds) => {
  if (!Array.isArray(lockIds)) {
    return [];
  }

  return lockIds
    .map((lockId) => Number.parseInt(lockId, 10))
    .filter((lockId) => Number.isFinite(lockId) && lockId > 0);
};

export const fetchJustLockResource = async (resourceAddress) => {
  const response = await fetch(`${JUSTLOCK_API_BASE_URL}/resource/${resourceAddress}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch JustLock resource (${response.status})`);
  }

  return response.json();
};

export const fetchJustLockLocks = async (lockIds) => {
  const parsedLockIds = parseLockIds(lockIds);
  if (!parsedLockIds.length) {
    return [];
  }

  const idsParam = encodeURIComponent(parsedLockIds.join(','));
  const response = await fetch(`${JUSTLOCK_API_BASE_URL}/lock?ids=${idsParam}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch JustLock lock details (${response.status})`);
  }

  const payload = await response.json();
  return Array.isArray(payload?.locks) ? payload.locks : [];
};

export const buildJustLockSnapshot = ({
  resourceData,
  lockDetails = [],
  resourceAddress,
  totalSupply = 0,
  fallbackTotalLocked = 0,
  fallbackForeverLocked = 0,
}) => {
  const summaryLocked = parseFiniteNumber(resourceData?.lock_summary?.amount);
  const lockIds = parseLockIds(resourceData?.lock_summary?.lock_ids);

  let totalLocked = summaryLocked > 0 ? summaryLocked : parseFiniteNumber(fallbackTotalLocked);

  const nowSeconds = Math.floor(Date.now() / 1000);
  let foreverLockedFromDetails = 0;
  let timeLockedFromDetails = 0;

  lockDetails.forEach((lock) => {
    const balance = getResourceBalance(lock, resourceAddress);
    if (balance <= 0) {
      return;
    }

    const unlockedAt = parseFiniteNumber(lock?.unlocked_at);
    if (unlockedAt > 0 && unlockedAt <= nowSeconds) {
      return;
    }

    const unlockableAt = parseFiniteNumber(lock?.unlockable_at);
    if (unlockableAt <= 0) {
      foreverLockedFromDetails += balance;
      return;
    }

    timeLockedFromDetails += balance;
  });

  const detailedTotal = foreverLockedFromDetails + timeLockedFromDetails;
  if (totalLocked <= 0 && detailedTotal > 0) {
    totalLocked = detailedTotal;
  }

  if (totalSupply > 0) {
    totalLocked = clamp(totalLocked, 0, totalSupply);
  } else {
    totalLocked = Math.max(0, totalLocked);
  }

  const hasDetailBreakdown = detailedTotal > 0;
  const fallbackForever = parseFiniteNumber(fallbackForeverLocked);
  const foreverLocked = hasDetailBreakdown
    ? clamp(foreverLockedFromDetails, 0, totalLocked)
    : clamp(fallbackForever, 0, totalLocked);
  const timeLocked = Math.max(0, totalLocked - foreverLocked);

  return {
    totalLocked,
    foreverLocked,
    timeLocked,
    hasDetailBreakdown,
    lockIds,
    lockCount: lockDetails.length,
  };
};
